import {LitElement, html, css} from 'lit';
import {morphedUpdate} from '../../services/morphed-entity.service.js';
import {morphedRead} from '../../auth/morphed-client.js';
import {clamBackend} from '../../services/clam-backend.service.js';

interface Evento {
	id: string;
	eventName: string;
	formCsvUrl?: string;
	inscritos?: string[];
}

export class ClamPeopleLoader extends LitElement {
	static styles = css`
		:host { display: block; padding: var(--clam-space-6); max-width: 900px; margin: 0 auto; font-family: var(--clam-font); color: var(--clam-text); }
		h2 { color: var(--clam-text); margin: 0 0 var(--clam-space-2); font-size: var(--clam-text-2xl); }
		p.desc { color: var(--clam-text-muted); font-size: var(--clam-text-base); margin: 0 0 var(--clam-space-6); }
		.field { margin-bottom: var(--clam-space-5); }
		label { display: block; font-size: var(--clam-text-sm); font-weight: 600; color: var(--clam-text); margin-bottom: 6px; }
		select, input { width: 100%; padding: 10px 12px; border: 1px solid var(--clam-border); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); box-sizing: border-box; font-family: inherit; color: var(--clam-text); background: var(--clam-surface); }
		select:focus, input:focus { outline: none; border-color: var(--clam-primary); box-shadow: 0 0 0 3px rgba(90,157,30,0.15); }
		.stats { display: flex; gap: var(--clam-space-4); margin: var(--clam-space-4) 0; flex-wrap: wrap; }
		.stat { background: var(--clam-surface-soft); border-radius: var(--clam-radius-md); padding: var(--clam-space-3) var(--clam-space-4); flex: 1 1 120px; min-width: 120px; }
		.stat-num { font-size: var(--clam-text-2xl); font-weight: 700; color: var(--clam-primary); }
		.stat-label { font-size: var(--clam-text-xs); color: var(--clam-text-muted); text-transform: uppercase; }
		.btn { padding: 10px var(--clam-space-5); border: none; border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); font-weight: 600; cursor: pointer; font-family: inherit; min-height: 40px; }
		.btn-primary { background: var(--clam-primary); color: var(--clam-on-primary); }
		.btn-primary:hover { background: var(--clam-primary-hover); }
		.btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
		.btn-secondary { background: var(--clam-surface-soft); color: var(--clam-primary); }
		.actions { display: flex; gap: var(--clam-space-3); margin-top: var(--clam-space-4); flex-wrap: wrap; }
		.msg { padding: var(--clam-space-3) var(--clam-space-4); border-radius: var(--clam-radius-md); font-size: var(--clam-text-base); margin-top: var(--clam-space-4); }
		.msg-ok { background: #e8f8e8; color: var(--clam-green-dark); }
		.msg-err { background: #fde8e8; color: var(--clam-danger); }
		.msg-info { background: #e8f4fd; color: var(--clam-green-dark); }
		.spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid var(--clam-border); border-top-color: var(--clam-primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
		@keyframes spin { to { transform: rotate(360deg); } }
	`;

	private _eventos: Evento[] = [];
	private _selectedId = '';
	private _csvUrl = '';
	private _loading = false;
	private _msg: {type: string; text: string} | null = null;

	connectedCallback(): void {
		super.connectedCallback();
		void this._loadEventos();
	}

	private async _loadEventos(): Promise<void> {
		this._loading = true;
		try {
			const eventos = await morphedRead<Evento>('Clam Evento', {options: {limit: 50}});
			this._eventos = eventos;
			if (eventos.length > 0) {
				this._selectedId = eventos[0].id;
				this._csvUrl = (eventos[0].formCsvUrl as string) || '';
			}
		} catch (e) {
			this._msg = {type: 'err', text: `Error cargando eventos: ${e instanceof Error ? e.message : String(e)}`};
		}
		this._loading = false;
		this.requestUpdate();
	}

	private _onSelect(e: Event): void {
		const id = (e.target as HTMLSelectElement).value;
		this._selectedId = id;
		const ev = this._eventos.find((x) => x.id === id);
		this._csvUrl = (ev?.formCsvUrl as string) || '';
		this._msg = null;
		this.requestUpdate();
	}

	/** True si la URL es un CSV público válido (export o pub), no la del editor /edit. */
	private _isValidCsvUrl(url: string): boolean {
		const u = url.trim();
		if (!u || !/^https:\/\/docs\.google\.com\/spreadsheets\//i.test(u)) return false;
		if (/\/edit\b/i.test(u)) return false;
		const isPub = /\/d\/e\/[^/]+\/pub\b/.test(u) && /[?&]output=csv\b/.test(u);
		const isExport = /\/d\/[^/]+\/export\b/.test(u) && /[?&]format=csv\b/.test(u);
		return isPub || isExport;
	}

	private async _saveCsvUrl(): Promise<void> {
		if (!this._selectedId) return;
		const ev = this._eventos.find((x) => x.id === this._selectedId);
		if (!ev) return;
		if (this._csvUrl && !this._isValidCsvUrl(this._csvUrl)) {
			this._msg = {type: 'err', text: 'La URL no es un CSV público válido. Usa export?format=csv (con el sheet compartido "Cualquiera con el enlace") o pub?output=csv (Publicar en la web). La URL del editor /edit no sirve.'};
			this.requestUpdate();
			return;
		}
		this._loading = true;
		this._msg = null;
		try {
			await morphedUpdate('Clam Evento', this._selectedId, {eventName: ev.eventName, formCsvUrl: this._csvUrl});
			ev.formCsvUrl = this._csvUrl;
			this._msg = {type: 'ok', text: 'URL del Google Sheets guardada.'};
		} catch (e) {
			this._msg = {type: 'err', text: `Error guardando: ${e instanceof Error ? e.message : String(e)}`};
		}
		this._loading = false;
		this.requestUpdate();
	}

	private async _sync(): Promise<void> {
		if (!this._selectedId) return;
		if (!this._csvUrl) {
			this._msg = {type: 'err', text: 'Primero pega la URL del Google Sheets publicado como CSV.'};
			this.requestUpdate();
			return;
		}
		this._loading = true;
		this._msg = {type: 'info', text: 'Sincronizando desde Google Sheets...'};
		this.requestUpdate();
		const res = await clamBackend.syncCsv(this._selectedId);
		if (res.ok && res.data) {
			const d = res.data;
			const created = (d.created as number) ?? 0;
			const updated = (d.updated as number) ?? 0;
			const total = (d.total as number) ?? 0;
			this._msg = {type: 'ok', text: `Sincronización completa: ${created} nuevas, ${updated} actualizadas, ${total} total inscritos.`};
			await this._loadEventos();
		} else {
			this._msg = {type: 'err', text: res.error || 'Error en la sincronización.'};
		}
		this._loading = false;
		this.requestUpdate();
	}

	render() {
		const ev = this._eventos.find((x) => x.id === this._selectedId);
		return html`
			<h2>Importar Asistentes</h2>
			<p class="desc">Pega la URL del Google Sheet como CSV público y sincroniza para crear/actualizar personas e inscribirlas al evento. Dos formas válidas:<br/>
			&nbsp;• <b>Export</b>: comparte el sheet "Cualquiera con el enlace" y usa <code>.../d/&lt;id&gt;/export?format=csv&amp;gid=&lt;gid&gt;</code><br/>
			&nbsp;• <b>Publicar</b>: Archivo → Compartir → Publicar en la web → CSV → <code>.../d/e/&lt;id&gt;/pub?output=csv</code><br/>
			La URL del editor (<code>/edit</code>) no sirve. Al guardar una URL válida, el backend la sincroniza automáticamente cada 5 min.</p>
			<div class="field">
				<label>Evento</label>
				<select @change=${this._onSelect} .value=${this._selectedId}>
					${this._eventos.map((e) => html`<option value=${e.id}>${e.eventName}</option>`)}
				</select>
			</div>
			<div class="field">
				<label>URL del Google Sheets (CSV)</label>
				<input type="url" placeholder="https://docs.google.com/spreadsheets/d/e/.../pub?output=csv" .value=${this._csvUrl} @input=${(e: Event) => { this._csvUrl = (e.target as HTMLInputElement).value; }} ?disabled=${this._loading} />
			</div>
			${ev ? html`
				<div class="stats">
					<div class="stat"><div class="stat-num">${(ev.inscritos || []).length}</div><div class="stat-label">Inscritos</div></div>
				</div>
			` : ''}
			<div class="actions">
				<button class="btn btn-secondary" @click=${() => this._saveCsvUrl()} ?disabled=${this._loading || !this._selectedId}>
					${this._loading ? html`<span class="spinner"></span>` : ''} Guardar URL
				</button>
				<button class="btn btn-primary" @click=${() => this._sync()} ?disabled=${this._loading || !this._csvUrl}>
					${this._loading ? html`<span class="spinner"></span>` : ''} Sincronizar personas
				</button>
			</div>
			${this._msg ? html`<div class="msg msg-${this._msg.type}">${this._msg.text}</div>` : ''}
		`;
	}
}

customElements.define('clam-people-loader', ClamPeopleLoader);
